import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Star, Eye, EyeOff, GripVertical } from "lucide-react";
import { API, authHeaders, imgUrl, formatApiError } from "../../lib/api";
import { useContent } from "../../context/ContentContext";

const EMPTY = {
  name: "",
  line: "",
  format: "inyectable",
  concentration: "",
  presentation: "",
  active_ingredient: "",
  description: "",
  image: "",
  featured: false,
  active: true,
};

const inputCls =
  "w-full rounded-xl border border-[#DDD8C8] bg-white px-4 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";
const labelCls = "mb-1 block text-xs font-bold uppercase tracking-wider text-neutral-500";

export default function ProductsTab() {
  const { lines, getLine } = useContent();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/products`, authHeaders())
      .then((res) => setProducts(res.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => {
    setForm({ ...EMPTY, line: lines[0]?.id || "" });
    setEditing("new");
  };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p });
    setEditing(p.id);
  };

  const toggleActive = async (p) => {
    const next = p.active === false ? true : false;
    try {
      await axios.put(`${API}/products/${p.id}`, { ...p, active: next }, authHeaders());
      toast.success(next ? `"${p.name}" visible en la web` : `"${p.name}" oculto de la web`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, authHeaders());
      setForm((f) => ({ ...f, image: data.path }));
      toast.success("Foto subida correctamente");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/products`, form, authHeaders());
        toast.success("Producto creado");
      } else {
        await axios.put(`${API}/products/${editing}`, form, authHeaders());
        toast.success("Producto actualizado");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/products/${p.id}`, authHeaders());
      toast.success("Producto eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const reordered = [...products];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setProducts(reordered);
    setDragIndex(null);
    setOverIndex(null);
    try {
      await axios.post(`${API}/products/reorder`, { ids: reordered.map((p) => p.id) }, authHeaders());
      toast.success("Orden del catálogo actualizado");
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div data-testid="products-tab">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-neutral-900">Productos ({products.length})</h2>
          <p className="text-sm text-neutral-500">
            Arrastre para ordenar el catálogo. Use el interruptor para ocultar un producto de la web sin eliminarlo.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="product-new-button"
          className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark"
        >
          <Plus size={16} /> Nuevo producto
        </button>
      </div>

      {loading ? (
        <p className="py-16 text-center font-mono text-xs uppercase tracking-widest text-neutral-400">Cargando…</p>
      ) : (
        <div className="grid gap-3" data-testid="admin-products-list">
          {products.map((p, i) => {
            const isActive = p.active !== false;
            return (
              <div
                key={p.id}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOverIndex(i);
                }}
                onDrop={() => onDrop(i)}
                onDragEnd={() => {
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                className={`flex items-center gap-4 rounded-2xl border bg-white p-3 shadow-sm transition-all ${
                  isActive ? "border-[#E8E4D9]" : "border-dashed border-neutral-300 opacity-60"
                } ${
                  overIndex === i && dragIndex !== null && dragIndex !== i
                    ? "border-brand ring-2 ring-brand/30"
                    : ""
                } ${dragIndex === i ? "opacity-50" : ""}`}
                data-testid={`admin-product-row-${p.id}`}
              >
                <span
                  className="cursor-grab text-neutral-300 hover:text-neutral-500 active:cursor-grabbing"
                  data-testid={`product-drag-${p.id}`}
                  title="Arrastrar para reordenar"
                >
                  <GripVertical size={18} />
                </span>
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-ivory">
                  {p.image && <img src={imgUrl(p.image)} alt={p.name} className="h-full w-full object-contain" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-neutral-900">
                    {p.featured && <Star size={12} className="mr-1 inline text-brand" fill="currentColor" />}
                    {p.name}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">
                    {getLine(p.line).name} · {p.concentration} · {p.format}
                  </p>
                </div>
                <button
                  onClick={() => toggleActive(p)}
                  data-testid={`product-toggle-${p.id}`}
                  title={isActive ? "Ocultar de la web" : "Mostrar en la web"}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                    isActive
                      ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      : "bg-neutral-200 text-neutral-500 hover:bg-neutral-300"
                  }`}
                >
                  {isActive ? <Eye size={13} /> : <EyeOff size={13} />}
                  {isActive ? "Activo" : "Inactivo"}
                </button>
                <button
                  onClick={() => openEdit(p)}
                  data-testid={`product-edit-${p.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-4 py-2 text-xs font-bold text-neutral-600 hover:border-brand hover:text-brand"
                >
                  <Pencil size={13} /> Editar
                </button>
                <button
                  onClick={() => remove(p)}
                  data-testid={`product-delete-${p.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-500 hover:bg-red-50"
                >
                  <Trash2 size={13} /> Eliminar
                </button>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={() => setEditing(null)}
            data-testid="product-form-backdrop"
          >
            <motion.form
              onSubmit={save}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl sm:p-8"
              data-testid="product-form"
            >
              <div className="mb-6 flex items-center justify-between">
                <h3 className="font-display text-lg font-bold text-neutral-900">
                  {editing === "new" ? "Nuevo producto" : "Editar producto"}
                </h3>
                <button type="button" onClick={() => setEditing(null)} data-testid="product-form-close" className="rounded-full bg-neutral-100 p-2 hover:bg-neutral-200">
                  <X size={16} />
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Nombre del producto *</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="product-form-name" />
                </div>
                <div>
                  <label className={labelCls}>Línea *</label>
                  <select required value={form.line} onChange={(e) => setForm({ ...form, line: e.target.value })} className={inputCls} data-testid="product-form-line">
                    {lines.length === 0 && <option value="">Sin líneas</option>}
                    {lines.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Formato *</label>
                  <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className={inputCls} data-testid="product-form-format">
                    <option value="inyectable">Inyectable</option>
                    <option value="comprimidos">Comprimidos</option>
                    <option value="suplemento">Suplemento</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Concentración *</label>
                  <input required value={form.concentration} onChange={(e) => setForm({ ...form, concentration: e.target.value })} className={inputCls} placeholder="250 mg/mL" data-testid="product-form-concentration" />
                </div>
                <div>
                  <label className={labelCls}>Presentación *</label>
                  <input required value={form.presentation} onChange={(e) => setForm({ ...form, presentation: e.target.value })} className={inputCls} placeholder="Caja x 10 ampollas" data-testid="product-form-presentation" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Principio activo *</label>
                  <input required value={form.active_ingredient} onChange={(e) => setForm({ ...form, active_ingredient: e.target.value })} className={inputCls} data-testid="product-form-ingredient" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Descripción *</label>
                  <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="product-form-description" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Foto del producto</label>
                  <div className="flex items-center gap-4">
                    <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl border border-[#E8E4D9] bg-ivory" data-testid="product-form-photo-preview">
                      {form.image ? (
                        <img src={imgUrl(form.image)} alt="preview" className="h-full w-full object-contain" />
                      ) : (
                        <Upload size={20} className="text-neutral-300" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} data-testid="product-form-file" />
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="product-form-upload-button"
                        className="flex items-center gap-2 rounded-full border border-brand px-4 py-2 text-xs font-bold text-brand hover:bg-brand-light disabled:opacity-60"
                      >
                        <Upload size={13} /> {uploading ? "Subiendo…" : "Subir foto"}
                      </button>
                      <input
                        value={form.image.startsWith("http") ? form.image : ""}
                        onChange={(e) => setForm({ ...form, image: e.target.value })}
                        className={inputCls}
                        placeholder="…o pegue una URL de imagen"
                        data-testid="product-form-image-url"
                      />
                    </div>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#B8912F]" data-testid="product-form-featured" />
                  Producto destacado
                </label>
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#B8912F]" data-testid="product-form-active" />
                  Activo (visible en la web)
                </label>
              </div>

              <button
                type="submit"
                disabled={saving || uploading}
                data-testid="product-form-save"
                className="mt-6 w-full rounded-full bg-brand py-3.5 text-sm font-bold text-white hover:bg-brand-dark disabled:opacity-60"
              >
                {saving ? "Guardando…" : "Guardar producto"}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
